import { Component } from '@theme/component';

export function buildInquiryPayload(data, pageUri, pageName) {
  const fields = ['firstname', 'lastname', 'email', 'phone', 'message']
    .map((name) => ({ objectTypeId: '0-1', name, value: String(data.get(name) || '').trim() }))
    .filter((field) => field.value);
  const url = new URL(pageUri);
  return { fields, context: { pageUri: url.origin + url.pathname, pageName } };
}

export async function sendInquiry(portalId, formId, payload, signal) {
  if (!/^\d+$/.test(portalId) || !/^[a-f0-9-]{36}$/.test(formId)) throw new Error('Invalid form configuration');
  const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify(payload),
    signal,
  });
  if (response.status === 200) return { success: true };
  if (response.status === 429) return { success: false, reason: 'rateLimit' };
  if (response.status === 400) {
    const body = await response.json().catch(() => ({}));
    const emailError = body.errors?.some((error) => ['INVALID_EMAIL', 'BLOCKED_EMAIL'].includes(error.errorType));
    return { success: false, reason: emailError ? 'invalidEmail' : 'error' };
  }
  return { success: false, reason: 'error' };
}

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLFieldSetElement} fields
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} status
 * @extends {Component<Refs>}
 */
class DiscoveryInquiry extends Component {
  requiredRefs = ['form', 'fields', 'submit', 'status'];
  #listeners;
  #request;
  #pending = false;
  #completed = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.refs.form.addEventListener('submit', this.handleSubmit, { signal: this.#listeners.signal });
    this.refs.fields.disabled = this.#completed;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
  }

  /** @param {SubmitEvent} event */
  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#pending || this.#completed || !this.refs.form.reportValidity()) return;
    this.#pending = true;
    const { form, fields, submit, status } = this.refs;
    const originalLabel = submit.textContent;
    const payload = buildInquiryPayload(new FormData(form), window.location.href, document.title);
    this.#request = new AbortController();
    const request = this.#request;
    const timer = setTimeout(() => request.abort(), 20000);
    fields.disabled = true;
    form.setAttribute('aria-busy', 'true');
    submit.textContent = this.dataset.sending;
    status.textContent = '';
    try {
      const result = await sendInquiry(this.dataset.portalId, this.dataset.formId, payload, request.signal);
      if (!this.isConnected) return;
      if (result.success) {
        this.#completed = true;
        form.hidden = true;
        status.textContent = `${this.dataset.success} ${this.dataset.successDetail}`;
      } else {
        status.textContent = this.dataset[result.reason] || this.dataset.error;
      }
      status.focus();
    } catch {
      if (this.isConnected) {
        status.textContent = this.dataset.error;
        status.focus();
      }
    } finally {
      clearTimeout(timer);
      this.#pending = false;
      if (this.#request === request) this.#request = undefined;
      fields.disabled = this.#completed || !this.isConnected;
      form.removeAttribute('aria-busy');
      submit.textContent = originalLabel;
    }
  };
}

if (!customElements.get('zu-discovery-form')) customElements.define('zu-discovery-form', DiscoveryInquiry);
